import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { societyApi } from '@/features/societies/api/societyApi';
import type { EffectiveConfiguration } from '@/features/societies/types/society-configuration.types';

export type { EffectiveConfiguration };

/**
 * Hook pour recuperer la configuration effective (merge groupe + propre)
 */
export const useEffectiveConfiguration = (societyId: string | undefined, options?: { enabled?: boolean }) => {
  return useQuery<EffectiveConfiguration>({
    queryKey: ['society-configuration', 'effective', societyId],
    queryFn: async () => {
      if (!societyId) throw new Error('Society ID required');
      const response = await societyApi.getEffectiveConfiguration(societyId);
      return response.data;
    },
    enabled: !!societyId && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook pour récupérer la configuration groupe
 */
export const useGroupConfiguration = (societyId: string | undefined) => {
  return useQuery({
    queryKey: ['society-configuration', 'group', societyId],
    queryFn: async () => {
      if (!societyId) throw new Error('Society ID required');
      const response = await societyApi.getGroupConfiguration(societyId);
      return response.data;
    },
    enabled: !!societyId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook pour récupérer la configuration propre
 */
export const useOwnConfiguration = (societyId: string | undefined) => {
  return useQuery({
    queryKey: ['society-configuration', 'own', societyId],
    queryFn: async () => {
      if (!societyId) throw new Error('Society ID required');
      const response = await societyApi.getOwnConfiguration(societyId);
      return response.data;
    },
    enabled: !!societyId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook pour mettre à jour la configuration groupe
 */
export const useUpdateGroupConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ societyId, updates }: { societyId: string; updates: any }) =>
      societyApi.updateGroupConfiguration(societyId, updates),
    onSuccess: (_, { societyId }) => {
      queryClient.invalidateQueries({ queryKey: ['society-configuration', 'group', societyId] });
      queryClient.invalidateQueries({ queryKey: ['society-configuration', 'effective', societyId] });
    },
  });
};

/**
 * Hook pour mettre à jour la configuration propre
 */
export const useUpdateOwnConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ societyId, updates }: { societyId: string; updates: any }) =>
      societyApi.updateOwnConfiguration(societyId, updates),
    onSuccess: (_, { societyId }) => {
      queryClient.invalidateQueries({ queryKey: ['society-configuration', 'own', societyId] });
      queryClient.invalidateQueries({ queryKey: ['society-configuration', 'effective', societyId] });
    },
  });
};

/**
 * Hook pour détacher une société de la configuration groupe
 */
export const useDetachFromGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (societyId: string) => societyApi.detachFromGroup(societyId),
    onSuccess: (_, societyId) => {
      queryClient.invalidateQueries({ queryKey: ['society-configuration', 'group', societyId] });
      queryClient.invalidateQueries({ queryKey: ['society-configuration', 'own', societyId] });
      queryClient.invalidateQueries({ queryKey: ['society-configuration', 'effective', societyId] });
      queryClient.invalidateQueries({ queryKey: ['society', societyId] });
    },
  });
};

/**
 * Hook pour définir l'autonomie de facturation d'un membre.
 * target : 'SELF' (hors groupe) | 'INHERIT' (hérite du groupe) | UUID d'un ancêtre.
 */
export const useSetBillingAutonomy = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ societyId, target }: { societyId: string; target: string }) =>
      societyApi.setBillingAutonomy(societyId, target),
    onSuccess: (_, { societyId }) => {
      queryClient.invalidateQueries({ queryKey: ['society-configuration', 'own', societyId] });
      queryClient.invalidateQueries({ queryKey: ['society-configuration', 'effective', societyId] });
      queryClient.invalidateQueries({ queryKey: ['society', societyId] });
    },
  });
};
